import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { ErrorMessage } from "@ui/components/common/error-message";
import { TextInput } from "@ui/components/inputs/text-input";
import { row, stack } from "@ui/style";
import { isNotNil } from "@utils/assert";
import { REGEX_EMAIL } from "@utils/regex-utils";
import { Button } from "primereact/button";
import React from "react";
import { Controller, useForm } from "react-hook-form";

interface EmailFormData {
	email: string;
}

interface SharingUsingEmailFormProps {
	defaultEmail?: string;
	onSuccess?: (email: string) => Promise<void> | void;
}

export const SharingUsingEmailForm = ({ defaultEmail, onSuccess }: SharingUsingEmailFormProps): JSX.Element => {
	const { format } = useI18n();

	const { control: controlEmail, handleSubmit: handleEmailSubmit } = useForm<EmailFormData>({
		defaultValues: {
			email: defaultEmail,
		},
	});

	const [submitEmail, isLoading, isError] = useAsyncOperation(
		async (data: EmailFormData) => {
			await onSuccess?.(data.email);
		},
		{ minimumDuration: 500 },
	);

	return (
		<Form onSubmit={handleEmailSubmit(submitEmail)}>
			<FormField>
				<label className="p-size-m">{format("home.pendingPatientDialog.details.sharing.email.label")}</label>
				<Controller
					name="email"
					control={controlEmail}
					rules={{
						required: format("global.form.field.requiredError"),
						pattern: {
							value: REGEX_EMAIL,
							message: format("home.pendingPatientDialog.details.sharing.email.incorrectFormat"),
						},
					}}
					render={({ field, fieldState }) => (
						<>
							<TextInput
								{...field}
								error={isNotNil(fieldState.error)}
								placeholder={format("home.pendingPatientDialog.details.sharing.email.placeholder")}
								autoCapitalize="words"
							/>
							<ErrorMessage>{isError || fieldState.error?.message}</ErrorMessage>
						</>
					)}
				/>
			</FormField>
			<ButtonContainer>
				<Button
					loading={isLoading}
					label={format("home.pendingPatientDialog.details.sharing.email.submitButton")}
					type="submit"
				/>
			</ButtonContainer>
		</Form>
	);
};

const ButtonContainer = styled.div`
	padding-top: 0.75rem;
	${row(undefined, "flexend")}
`;

const Form = styled.form`
	${stack("M", "flexstart", "stretch")}
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;
